import React, { useState, useEffect, useMemo } from 'react';
import { useAnalysisStore } from '../../store/analysisStore';
import styled from 'styled-components';
// Link importieren
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FiHome,
  FiUser,
  FiUserPlus,
  FiLogOut,
  FiSearch,
  FiPlus,
  FiMenu,
  FiX,
  FiArchive,
  // NEU: Icons für rechtliche Links (optional)
  FiFileText,
  FiShield,
} from 'react-icons/fi';
// theme importieren, falls noch nicht geschehen
import { theme } from '../../styles/theme';
import { getChats, createChat, archiveChat } from '../../api/chats';
import Button from '../common/Button';
import Input from '../common/Input';
import { useChatStore } from '../../store/chatStore';
import { formatDateTime } from '../../utils/formatting';
import { toast } from 'sonner';

const Sidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setWizardOpen } = useAnalysisStore();
  const { setChats, searchTerm, setSearchTerm, chats } = useChatStore();
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Überprüfen der Bildschirmgröße beim Laden und bei Resize
  useEffect(() => {
    const checkScreenSize = () => {
      const mobileBreakpoint = 992; // Typischer Wert für lg
      const isMobileView = window.innerWidth < mobileBreakpoint;
      setIsMobile(isMobileView);

      // Bei erstem Laden auf Mobilgeräten automatisch einklappen
      if (isMobileView && !isCollapsed) {
        // Nur einklappen, wenn es nicht bereits eingeklappt ist
        // setIsCollapsed(true); // Temporär deaktiviert, kann zu unerwartetem Verhalten führen
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [isCollapsed]); // isCollapsed zur Dependency List hinzugefügt

  // Toggle-Funktion für die Sidebar
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Fetch chats
  const {
    data: chatsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['chats'],
    queryFn: getChats,
    onSuccess: (data) => {
      // Sort chats by updatedAt descending before setting
      const sortedChats = [...(data || [])].sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
      setChats(sortedChats);
    },
  });

  // Archive chat mutation
  const archiveMutation = useMutation({
    mutationFn: archiveChat,
    onSuccess: (data, variables) => {
      toast.success('Chat erfolgreich archiviert');
      queryClient.invalidateQueries({ queryKey: ['chats'] });

      // Wenn der aktuell geöffnete Chat archiviert wurde, zur Startseite navigieren
      const currentChatId = location.pathname.split('/').pop();
      if (
        currentChatId &&
        location.pathname.includes('/app/chat/') &&
        currentChatId === String(variables) // variables ist die chatId
      ) {
        navigate('/app');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Fehler beim Archivieren des Chats');
    },
  });

  // Funktion zum Archivieren eines Chats
  const handleArchiveChat = (e, chatId) => {
    e.preventDefault(); // Verhindert die Navigation zum Chat
    e.stopPropagation(); // Verhindert Bubble-Up des Events

    if (window.confirm('Möchten Sie diesen Chat wirklich archivieren?')) {
      archiveMutation.mutate(chatId);
    }
  };

  // Calculate filtered chats using useMemo
  const filteredChatsList = useMemo(() => {
    // Verwende direkt die Daten aus der Abfrage
    const currentChats = Array.isArray(chatsData) ? chatsData : [];
    // Filtere archivierte Chats heraus
    const activeChats = currentChats.filter(chat => !chat.isArchived);
    if (!searchTerm) return activeChats;
    return activeChats.filter((chat) =>
      chat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [chatsData, searchTerm]);

  // Handle creating a new chat directly
  const handleCreateNewChat = async () => {
    setIsCreatingChat(true);
    try {
      const result = await createChat({ name: 'Neue Analyse' }); // Standardnamen übergeben
      toast.success('Neue Analyse gestartet');
      await queryClient.invalidateQueries({ queryKey: ['chats'] }); // Warten bis Query invalidiert ist
      refetch(); // Chats neu laden
      navigate(`/app/chat/${result.id}`);

      // Öffnet das Wizard-Modal
      setWizardOpen(true);
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error(
        'Fehler beim Erstellen der Analyse: ' +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setIsCreatingChat(false);
    }
  };

  return (
    <>
      {/* Mobile Toggle Button - immer sichtbar auf mobilen Geräten */}
      {isMobile && (
        <MobileToggleButton onClick={toggleSidebar}>
          {isCollapsed ? <FiMenu size={24} /> : <FiX size={24} />}
        </MobileToggleButton>
      )}

      <SidebarContainer isCollapsed={isCollapsed} isMobile={isMobile}>
        {/* Nur im ausgeklappten Zustand oder auf Desktop den Close-Button anzeigen */}
        {!isCollapsed && !isMobile && (
          <ToggleButton onClick={toggleSidebar}>
            <FiX size={20} />
          </ToggleButton>
        )}

        <LogoContainer>
          <Logo>{isCollapsed && !isMobile ? 'L-AI' : 'Law-AI'}</Logo>
          {(!isCollapsed || !isMobile) && (
            <Subtitle>by Phoenix Consulting</Subtitle>
          )}
        </LogoContainer>

        <SidebarContent>
          {/* Im eingeklappten Zustand auf Desktop die Icons anzeigen */}
          {isCollapsed && !isMobile ? (
            <CollapsedButtons>
              <CollapsedButton onClick={handleCreateNewChat} title="Neue Analyse">
                <FiPlus size={20} />
              </CollapsedButton>
              <CollapsedButton
                as={Link}
                to="/profile"
                $active={location.pathname === '/profile'}
                title="Profil"
              >
                <FiUser size={20} />
              </CollapsedButton>
              {user?.role === 'admin' && (
                <CollapsedButton
                  as={Link}
                  to="/admin"
                  $active={location.pathname.startsWith('/admin')}
                  title="Administration"
                >
                  <FiUserPlus size={20} />
                </CollapsedButton>
              )}
               {/* NEU: Icons für rechtliche Links im eingeklappten Zustand */}
               <CollapsedButton
                  as={Link}
                  to="/impressum"
                  $active={location.pathname === '/impressum'}
                  title="Impressum"
                >
                  <FiFileText size={20} />
                </CollapsedButton>
                <CollapsedButton
                  as={Link}
                  to="/datenschutz"
                  $active={location.pathname === '/datenschutz'}
                  title="Datenschutz"
                >
                  <FiShield size={20} />
                </CollapsedButton>
              <CollapsedButton as="button" onClick={onLogout} title="Abmelden">
                <FiLogOut size={20} />
              </CollapsedButton>
            </CollapsedButtons>
          ) : (
            /* Normale Darstellung im ausgeklappten Zustand */
            <>
              <NewChatSection>
                <Button
                  variant="primary"
                  fullWidth
                  onClick={handleCreateNewChat}
                  isLoading={isCreatingChat}
                >
                  <FiPlus /> Neue Analyse starten
                </Button>
              </NewChatSection>

              <SearchContainer>
                <SearchIconWrapper>
                  <FiSearch size={18} />
                </SearchIconWrapper>
                <SearchInput
                  placeholder="Chats durchsuchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchContainer>

              <Section>
                <SectionTitle>Ihre Chats</SectionTitle>
                {isLoading ? (
                  <LoadingText>Lade Chats...</LoadingText>
                ) : filteredChatsList.length > 0 ? (
                  <ChatList>
                    {filteredChatsList.map((chat) => (
                      <ChatItemContainer key={chat.id}>
                        <ChatItem
                          to={`/app/chat/${chat.id}`}
                          $active={location.pathname === `/app/chat/${chat.id}`}
                        >
                          <ChatName>{chat.name}</ChatName>
                          <ChatDate>{formatDateTime(chat.updatedAt)}</ChatDate>
                        </ChatItem>
                        <ArchiveButton
                          onClick={(e) => handleArchiveChat(e, chat.id)}
                          title="Chat archivieren"
                        >
                          <FiArchive size={16} />
                        </ArchiveButton>
                      </ChatItemContainer>
                    ))}
                  </ChatList>
                ) : (
                  <EmptyState>
                    {searchTerm
                      ? 'Keine Chats gefunden'
                      : 'Keine Chats vorhanden'}
                  </EmptyState>
                )}
              </Section>

              <SidebarFooter>
                {user?.role === 'admin' && (
                  <NavLink
                    to="/admin"
                    $active={location.pathname.startsWith('/admin')}
                  >
                    <FiUserPlus size={18} />
                    <span>Administration</span>
                  </NavLink>
                )}

                <NavLink
                  to="/profile"
                  $active={location.pathname === '/profile'}
                >
                  <FiUser size={18} />
                  <span>Profil</span>
                </NavLink>

                {/* NEU: Links zu Impressum und Datenschutz */}
                <LegalLinksContainer>
                  <NavLink to="/impressum" $active={location.pathname === '/impressum'}>
                     <FiFileText size={18} />
                     <span>Impressum</span>
                  </NavLink>
                  <NavLink to="/datenschutz" $active={location.pathname === '/datenschutz'}>
                     <FiShield size={18} />
                     <span>Datenschutz</span>
                  </NavLink>
                </LegalLinksContainer>

                <LogoutButton onClick={onLogout}>
                  <FiLogOut size={18} />
                  <span>Abmelden</span>
                </LogoutButton>

                <UserInfo>
                  <UserName>
                    {user?.firstName} {user?.lastName}
                  </UserName>
                  <UserRole>
                    {user?.role === 'admin' ? 'Administrator' : 'Benutzer'}
                  </UserRole>
                </UserInfo>
              </SidebarFooter>
            </>
          )}
        </SidebarContent>
      </SidebarContainer>

      {/* Overlay für mobile Ansicht im ausgeklappten Zustand */}
      {!isCollapsed && isMobile && <Overlay onClick={toggleSidebar} />}
    </>
  );
};

// --- Styled Components ---

// Neue Styled Components für den Archivierungsbutton
const ChatItemContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  margin-bottom: 0.25rem;
`;

const ArchiveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: transparent;
  color: ${theme.colors.textLight};
  border: none;
  cursor: pointer;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.backgroundAlt};
    color: ${theme.colors.primary};
  }

  ${ChatItemContainer}:hover & {
    opacity: 1;
  }
`;

// Bestehende Styled Components
const SidebarContainer = styled.aside`
  width: ${({ isCollapsed, isMobile }) =>
    isMobile ? '260px' : isCollapsed ? '80px' : '260px'}; // Breite für Icons angepasst
  height: 100vh;
  background-color: ${theme.colors.background};
  border-right: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: width 0.3s ease, transform 0.3s ease; // Transform hinzugefügt
  overflow-x: hidden;

  ${({ isMobile, isCollapsed }) =>
    isMobile &&
    isCollapsed &&
    `
    transform: translateX(-100%);
    width: 0; // Breite auf 0 setzen, wenn mobil und eingeklappt
  `}
  ${({ isMobile, isCollapsed }) =>
    isMobile &&
    !isCollapsed &&
    `
    transform: translateX(0);
    width: 260px; // Sicherstellen, dass Breite korrekt ist
  `}
`;

// Mobiler Toggle-Button (immer sichtbar)
const MobileToggleButton = styled.button`
  position: fixed;
  top: 15px; // Etwas nach unten verschoben
  left: 15px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  z-index: 1001; // Muss über Sidebar sein, wenn sie ausgeklappt ist
  box-shadow: ${theme.boxShadow.md};
`;

// Close-Button innerhalb der Sidebar
const ToggleButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${theme.colors.backgroundAlt}; // Hintergrund geändert
  color: ${theme.colors.text}; // Farbe geändert
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${theme.colors.borderLight}; // Rand hinzugefügt
  cursor: pointer;
  z-index: 1001; // Über dem Inhalt

  &:hover {
    background-color: ${theme.colors.borderLight};
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999; // Unter der Sidebar, aber über dem Hauptinhalt
`;

const LogoContainer = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${theme.colors.border};
  text-align: center;
  min-height: 70px; // Feste Höhe für Konsistenz
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Logo = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: ${theme.colors.primary};
  transition: all 0.3s ease; // Für sanften Übergang
`;

const Subtitle = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
  margin-top: 0.25rem;
  white-space: nowrap; // Verhindert Umbruch
  overflow: hidden; // Versteckt Überlauf
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ isCollapsed }) => (isCollapsed ? '1rem 0' : '1rem')}; // Padding anpassen
  display: flex;
  flex-direction: column; // Stellt sicher, dass Footer unten ist
`;

const CollapsedButtons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const CollapsedButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px; // Etwas größer für bessere Klickbarkeit
  height: 44px;
  border-radius: ${theme.borderRadius.md}; // Passend zum Rest
  color: ${({ $active }) =>
    $active ? theme.colors.primary : theme.colors.textLight}; // Farbe angepasst
  background-color: ${({ $active }) =>
    $active ? `${theme.colors.primary}15` : 'transparent'}; // Hintergrund angepasst
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none; // Für Link-Elemente

  &:hover {
    background-color: ${theme.colors.backgroundAlt};
    color: ${theme.colors.primary}; // Hover-Farbe
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textLight};
  margin: 0 0 0.75rem 0;
  padding: 0 0.5rem;
`;

const NewChatSection = styled(Section)`
  margin-bottom: 1rem;
  padding: 0 0.5rem; // Padding hinzugefügt
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
  padding: 0 0.5rem; // Padding hinzugefügt
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 1.25rem; // Angepasst an Padding
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textLight};
  pointer-events: none; // Klicks durchlassen
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 1rem 0.6rem 2.5rem; // Padding angepasst
  background-color: ${theme.colors.backgroundAlt};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  transition: border-color ${theme.transitions.fast};
  color: ${theme.colors.text};

  &::placeholder {
    color: ${theme.colors.textLight};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    background-color: ${theme.colors.background}; // Hintergrund bei Fokus ändern
  }
`;

const ChatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 0.5rem; // Padding hinzugefügt
`;

const ChatItem = styled(Link)`
  display: block;
  flex: 1;
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.md};
  text-decoration: none;
  color: ${theme.colors.text};
  transition: background-color ${theme.transitions.fast};
  background-color: ${({ $active }) =>
    $active ? theme.colors.backgroundAlt : 'transparent'};
  border-left: 3px solid
    ${({ $active }) => ($active ? theme.colors.primary : 'transparent')};
  position: relative; // Für den Archivierungsbutton

  &:hover {
    background-color: ${theme.colors.backgroundAlt};
    text-decoration: none;
  }
`;

const ChatName = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 30px; // Platz für Button lassen
`;

const ChatDate = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
  margin-top: 0.25rem;
`;

const SidebarFooter = styled.div`
  padding: 1rem 0.5rem; // Padding angepasst
  border-top: 1px solid ${theme.colors.border};
  margin-top: auto; // Bleibt unten
`;

// NEU: Container für rechtliche Links
const LegalLinksContainer = styled.div`
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid ${theme.colors.borderLight};
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.md};
  color: ${({ $active }) =>
    $active ? theme.colors.primary : theme.colors.text};
  background-color: ${({ $active }) =>
    $active ? `${theme.colors.primary}10` : 'transparent'};
  font-weight: ${({ $active }) => ($active ? '500' : 'normal')};
  margin-bottom: 0.25rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.backgroundAlt};
    text-decoration: none;
    color: ${theme.colors.primary}; // Hover-Farbe
  }

  span {
    margin-left: 0.75rem; // Etwas mehr Abstand
    white-space: nowrap; // Verhindert Umbruch
    overflow: hidden; // Versteckt Überlauf
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.danger};
  background-color: transparent;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-size: 1rem; // Angepasst an NavLink
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.danger}15; // Rötlicher Hintergrund bei Hover
    color: ${theme.colors.danger};
  }

  span {
    margin-left: 0.75rem;
  }
`;

const UserInfo = styled.div`
  margin-top: 1rem;
  padding-top: 1rem; // Padding oben
  border-top: 1px solid ${theme.colors.borderLight};
  text-align: center; // Zentriert
`;

const UserName = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
`;

const LoadingText = styled.div`
  padding: 0.75rem;
  font-size: 0.875rem;
  color: ${theme.colors.textLight};
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 1rem;
  text-align: center;
  color: ${theme.colors.textLight};
  font-size: 0.875rem;
`;

export default Sidebar;
